import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { 
  ShoppingCart, Eye, Package, Truck, CheckCircle, XCircle, Clock,
  ArrowLeft, PaperPlaneTilt, CircleNotch, Plus, ChatCircleDots,
  MapPin, CurrencyEur, Timer, FilePdf, Download
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  'on-hold': { label: 'En attente paiement', color: 'bg-amber-100 text-amber-800', icon: Clock },
  paid: { label: 'Payée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  confirmed: { label: 'Confirmée', color: 'bg-sky-100 text-sky-800', icon: CheckCircle },
  processing: { label: 'En traitement', color: 'bg-blue-100 text-blue-800', icon: Package },
  preparing: { label: 'En préparation', color: 'bg-blue-100 text-blue-800', icon: Package },
  shipping: { label: 'En livraison', color: 'bg-purple-100 text-purple-800', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  completed: { label: 'Terminée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800', icon: XCircle },
  refunded: { label: 'Remboursée', color: 'bg-orange-100 text-orange-800', icon: XCircle },
  failed: { label: 'Échouée', color: 'bg-red-100 text-red-800', icon: XCircle },
};

// ======== ORDER DETAIL WITH TIMELINE ========
const OrderDetail = ({ order, storeId, onClose, onUpdate }) => {
  const [historyNote, setHistoryNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState(order.status);
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '');

  const addHistory = async () => {
    if (!historyNote.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/stores/${storeId}/orders/${order.id}/history`,
        { status: order.status, notes: historyNote },
        { withCredentials: true }
      );
      toast.success('Note ajoutée');
      setHistoryNote('');
      onUpdate();
    } catch { toast.error('Erreur'); }
    finally { setAdding(false); }
  };

  const updateStatus = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/api/stores/${storeId}/orders/${order.id}/status`,
        { status: newStatus, tracking_number: trackingNumber, notes: `Statut changé vers ${statusConfig[newStatus]?.label}` },
        { withCredentials: true }
      );
      toast.success('Statut mis à jour');
      onUpdate();
    } catch { toast.error('Erreur'); }
    finally { setUpdating(false); }
  };

  const status = statusConfig[order.status] || statusConfig.pending;
  const history = order.history || [];

  return (
    <div className="space-y-6" data-testid="order-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose}><ArrowLeft size={20} /></Button>
          <div>
            <h2 className="text-xl font-bold">Commande {order.order_number}</h2>
            <p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleString('fr-FR')}</p>
          </div>
        </div>
        <Badge className={`${status.color} px-3 py-1 text-sm`}>{status.label}</Badge>
      </div>

      {/* Visual Timeline */}
      <Card>
        <CardHeader><CardTitle className="text-base">Suivi de la commande</CardTitle></CardHeader>
        <CardContent>
          {(() => {
            const STAGES = [
              { key: 'pending',    label: 'Bon de commande',  color: 'slate' },
              { key: 'confirmed',  label: 'Payée',            color: 'blue' },
              { key: 'preparing',  label: 'Préparation',      color: 'amber' },
              { key: 'shipping',   label: 'BL / Livraison',   color: 'indigo' },
              { key: 'delivered',  label: 'Livrée',           color: 'emerald' },
              { key: 'invoiced',   label: 'Facturée',         color: 'violet' },
            ];
            const colorMap = {
              slate: { bg: 'bg-slate-100', text: 'text-slate-600', bar: 'bg-slate-300', ring: 'ring-slate-300' },
              blue: { bg: 'bg-blue-100', text: 'text-blue-700', bar: 'bg-blue-500', ring: 'ring-blue-400' },
              amber: { bg: 'bg-amber-100', text: 'text-amber-700', bar: 'bg-amber-500', ring: 'ring-amber-400' },
              indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700', bar: 'bg-indigo-500', ring: 'ring-indigo-400' },
              emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-600', ring: 'ring-emerald-400' },
              violet: { bg: 'bg-violet-100', text: 'text-violet-700', bar: 'bg-violet-500', ring: 'ring-violet-400' },
            };
            const hbs = {};
            history.forEach(h => { if (h?.status && !hbs[h.status]) hbs[h.status] = h; });
            if (!hbs.pending) hbs.pending = { date: order.created_at };
            if (!hbs.confirmed && (order.payment_status === 'paid' || ['paid', 'completed'].includes(order.status))) {
              hbs.confirmed = { date: order.paid_at || order.created_at };
            }
            if (!hbs.invoiced && order.invoice_url) hbs.invoiced = { date: order.invoiced_at || order.created_at };
            const order_idx = STAGES.findIndex(s => s.key === order.status);
            const currentIdx = Math.max(
              order_idx,
              ...STAGES.map((s, i) => hbs[s.key] ? i : -1),
              0
            );
            return (
              <div className="flex items-start justify-between gap-2 overflow-x-auto px-2 pt-3 pb-4">
                {STAGES.map((stage, idx) => {
                  const reached = idx <= currentIdx;
                  const isCurrent = idx === currentIdx;
                  const entry = hbs[stage.key];
                  const c = colorMap[stage.color];
                  return (
                    <React.Fragment key={stage.key}>
                      <div className="flex flex-col items-center text-center min-w-[90px] flex-1" data-testid={`stage-${stage.key}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${reached ? c.bg + ' ' + c.text : 'bg-slate-50 text-slate-300'} ${isCurrent ? 'ring-2 ring-offset-2 ' + c.ring : ''}`}>
                          {idx + 1}
                        </div>
                        <p className={`text-[11px] font-semibold mt-2 ${reached ? c.text : 'text-slate-400'}`}>{stage.label}</p>
                        {entry?.date && <p className="text-[9px] text-slate-400 mt-0.5">{new Date(entry.date).toLocaleDateString('fr-FR')}</p>}
                      </div>
                      {idx < STAGES.length - 1 && (
                        <div className={`h-1 rounded flex-1 mt-[22px] ${idx < currentIdx ? colorMap[STAGES[idx + 1].color].bar : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Order info */}
        <div className="lg:col-span-2 space-y-5">
          {/* Items */}
          <Card>
            <CardHeader><CardTitle className="text-base">Articles ({order.items?.length || 0})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead>Qté</TableHead>
                    <TableHead>Prix unit.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {(order.items || []).map((item, i) => {
                  const productHref = item.slug ? `/product/${item.slug}` : null;
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {item.image && <img src={item.image} alt="" className="w-10 h-10 rounded object-cover" />}
                          <div>
                            {productHref ? (
                              <a
                                href={productHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-sm text-[#0052FF] hover:underline"
                                data-testid={`order-item-link-${i}`}
                              >
                                {item.name}
                              </a>
                            ) : (
                              <p className="font-medium text-sm">{item.name}</p>
                            )}
                            {item.variation && <p className="text-xs text-slate-500">{Object.entries(item.variation).map(([k, v]) => `${k}: ${v}`).join(', ')}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.price?.toFixed(2)} €</TableCell>
                      <TableCell className="text-right font-medium">{(item.price * item.quantity).toFixed(2)} €</TableCell>
                    </TableRow>
                  );
                })}
                </TableBody>
              </Table>
              <div className="border-t p-4 space-y-1 text-right">
                <div className="flex justify-between text-sm"><span>Sous-total</span><span>{order.subtotal?.toFixed(2) || order.total?.toFixed(2)} €</span></div>
                {order.discount_amount > 0 && <div className="flex justify-between text-sm text-emerald-600"><span>Réduction ({order.promo_code})</span><span>-{order.discount_amount?.toFixed(2)} €</span></div>}
                {order.loyalty_discount > 0 && <div className="flex justify-between text-sm text-purple-600"><span>Remise fidélité ({order.loyalty_tier_name})</span><span>-{order.loyalty_discount?.toFixed(2)} €</span></div>}
                <div className="flex justify-between text-sm"><span>Livraison</span><span>{order.shipping_cost > 0 ? `${order.shipping_cost?.toFixed(2)} €` : 'Gratuit'}</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total</span><span>{order.total?.toFixed(2)} €</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Update Status */}
          <Card>
            <CardHeader><CardTitle className="text-base">Mettre à jour</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Statut</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">N° de suivi</Label>
                  <Input value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Numéro de suivi" />
                </div>
              </div>
              <Button onClick={updateStatus} disabled={updating} className="bg-[#0052FF]" data-testid="update-order-status-btn">
                {updating ? <CircleNotch className="animate-spin mr-2" size={14} /> : <PaperPlaneTilt className="mr-2" size={14} />}
                Mettre à jour
              </Button>
            </CardContent>
          </Card>

          {/* History Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Timer size={18} />Historique ({history.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add note */}
              <div className="flex gap-2">
                <Input value={historyNote} onChange={e => setHistoryNote(e.target.value)} placeholder="Ajouter une note..." onKeyDown={e => e.key === 'Enter' && addHistory()} data-testid="history-note-input" />
                <Button onClick={addHistory} disabled={adding} size="sm" className="bg-[#0052FF]">
                  {adding ? <CircleNotch className="animate-spin" size={14} /> : <Plus size={14} />}
                </Button>
              </div>

              {/* Timeline */}
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucun historique</p>
              ) : (
                <div className="relative ml-3">
                  <div className="absolute left-1.5 top-0 bottom-0 w-0.5 bg-slate-200" />
                  {[...history].reverse().map((entry, i) => {
                    const entryStatus = statusConfig[entry.status] || {};
                    return (
                      <div key={i} className="relative pl-8 pb-4" data-testid={`history-entry-${i}`}>
                        <div className={`absolute left-0 top-1 w-3.5 h-3.5 rounded-full border-2 border-white ${entryStatus.color?.split(' ')[0] || 'bg-slate-300'}`} />
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-slate-700">{entryStatus.label || entry.status}</span>
                            <span className="text-xs text-slate-400">{new Date(entry.date).toLocaleString('fr-FR')}</span>
                          </div>
                          {entry.notes && <p className="text-sm text-slate-600">{entry.notes}</p>}
                          {entry.user_name && <p className="text-xs text-slate-400 mt-1">Par {entry.user_name}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Customer & Shipping */}
        <div className="space-y-5">
          {(() => {
            const ship = order.shipping_address || {};
            const bill = order.billing_address || {};
            // Fallback: use billing if shipping is empty (legacy WC orders)
            const hasShip = ship.first_name || ship.last_name || ship.address || ship.address_1;
            const a = hasShip ? ship : bill;
            const first = a.first_name || '';
            const last = a.last_name || '';
            const phone = a.phone || bill.phone || order.customer_phone;
            const email = a.email || bill.email || order.customer_email;
            const addr = a.address || a.address_1 || '';
            const addr2 = a.address_2 || '';
            const pc = a.postal_code || a.postcode || '';
            const city = a.city || '';
            const fullName = `${first} ${last}`.trim();
            return (
              <>
                <Card>
                  <CardHeader><CardTitle className="text-base">Client</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5">
                    <p className="font-semibold text-slate-900">{fullName || '—'}</p>
                    {email && <p className="text-sm text-slate-500 break-all">📧 {email}</p>}
                    {phone && (
                      <a href={`tel:${phone}`} className="text-sm text-blue-600 hover:underline block" data-testid="customer-phone">
                        📞 {phone}
                      </a>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} />{hasShip ? 'Adresse de livraison' : 'Adresse de facturation'}</CardTitle></CardHeader>
                  <CardContent className="text-sm text-slate-600 space-y-1">
                    {fullName && <p className="font-medium text-slate-800">{fullName}</p>}
                    {addr && <p>{addr}</p>}
                    {addr2 && <p>{addr2}</p>}
                    {(pc || city) && <p>{pc} {city}</p>}
                  </CardContent>
                </Card>

                {hasShip && bill.address_1 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} />Adresse de facturation</CardTitle></CardHeader>
                    <CardContent className="text-sm text-slate-600 space-y-1">
                      <p className="font-medium text-slate-800">{bill.first_name} {bill.last_name}</p>
                      <p>{bill.address_1}</p>
                      {bill.address_2 && <p>{bill.address_2}</p>}
                      <p>{bill.postcode || bill.postal_code} {bill.city}</p>
                    </CardContent>
                  </Card>
                )}
              </>
            );
          })()}

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Truck size={16} />Livraison</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-3">
              {/* Delivery address (different if specified) */}
              {order.delivery_address && order.delivery_address.city ? (
                <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
                  <p className="text-xs font-semibold text-amber-700 uppercase">Adresse de livraison (différente)</p>
                  {order.delivery_address.address && <p className="text-slate-700">{order.delivery_address.address}</p>}
                  <p className="font-medium text-slate-800">{order.delivery_address.postal_code} {order.delivery_address.city}</p>
                </div>
              ) : (
                <div className="p-2.5 bg-slate-50 rounded-lg space-y-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Livraison à</p>
                  <p className="text-slate-700">{order.shipping_address?.address}</p>
                  <p className="font-medium text-slate-800">{order.shipping_address?.postal_code} {order.shipping_address?.city}</p>
                </div>
              )}

              {/* Delivery date & slot */}
              {order.delivery_date ? (
                <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg" data-testid="delivery-date-block">
                  <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Date de livraison souhaitée</p>
                  <p className="font-bold text-blue-900">{new Date(order.delivery_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  {order.delivery_slot && <p className="text-blue-600 text-xs mt-0.5">Créneau : {order.delivery_slot}</p>}
                </div>
              ) : (
                <div className="p-2.5 bg-slate-50 border border-dashed border-slate-200 rounded-lg" data-testid="delivery-date-block">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Date de livraison souhaitée</p>
                  <p className="text-slate-400 italic text-xs">Non précisée par le client</p>
                </div>
              )}

              <p className="capitalize text-slate-500">{order.shipping_method?.replace(/_/g, ' ')}</p>

              {order.tracking_number && (
                <div className="p-2 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">N° de suivi</p>
                  <p className="text-blue-800 font-mono">{order.tracking_number}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><CurrencyEur size={16} />Paiement</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <p className="capitalize font-medium">{order.payment_method === 'stripe' ? 'Carte bancaire' : order.payment_method === 'paypal' ? 'PayPal' : order.payment_method === 'bank_transfer' ? 'Virement bancaire' : order.payment_method}</p>
              {order.payment_status && (
                <Badge className={order.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                  {order.payment_status === 'paid' ? 'Payé' : order.payment_status === 'pending' ? 'En attente' : order.payment_status}
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* PDF Downloads */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><FilePdf size={16} />Documents</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                data-testid="download-invoice-btn"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${API_URL}/api/stores/${storeId}/orders/${order.id}/invoice`;
                  link.download = `facture_${order.order_number}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download size={16} /> Facture PDF
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                data-testid="download-delivery-note-btn"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${API_URL}/api/stores/${storeId}/orders/${order.id}/delivery-note`;
                  link.download = `bon_livraison_${order.order_number}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download size={16} /> Bon de livraison PDF
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ======== MAIN ORDERS PAGE ========
const OrdersPage = () => {
  const { currentStore } = useStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!currentStore) return;
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/api/stores/${currentStore.id}/orders${params}`, { withCredentials: true });
      setOrders(response.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [currentStore, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const refreshAndReload = async () => {
    await fetchOrders();
    if (selectedOrder) {
      try {
        const resp = await axios.get(`${API_URL}/api/stores/${currentStore.id}/orders/${selectedOrder.id}`, { withCredentials: true });
        setSelectedOrder(resp.data);
      } catch { }
    }
  };

  if (!currentStore) return <div className="text-center py-12"><p className="text-slate-500">Sélectionnez une boutique</p></div>;

  if (selectedOrder) {
    return <OrderDetail order={selectedOrder} storeId={currentStore.id} onClose={() => setSelectedOrder(null)} onUpdate={refreshAndReload} />;
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Commandes</h1>
          <p className="text-slate-500 mt-1">{orders.length} commandes</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" data-testid="date-from" placeholder="Du" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" data-testid="date-to" placeholder="Au" />
          <Input value={productFilter} onChange={(e) => setProductFilter(e.target.value)} className="w-[180px]" placeholder="Filtrer par SKU ou nom" data-testid="product-filter" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="status-filter"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => {
            const params = new URLSearchParams();
            if (dateFrom) params.set('date_from', dateFrom);
            if (dateTo) params.set('date_to', dateTo);
            if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
            if (productFilter) params.set('product_id', productFilter);
            const url = `${API_URL}/api/admin/orders-export?${params.toString()}`;
            const a = document.createElement('a'); a.href = url; a.click();
          }} className="gap-2" data-testid="export-orders-csv">
            <Download size={16} /> Exporter CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto" /></div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <ShoppingCart size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucune commande</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Commande</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Articles</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Livraison</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => {
                  const st = statusConfig[order.status] || statusConfig.pending;
                  const StIcon = st.icon;
                  return (
                    <TableRow key={order.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedOrder(order)} data-testid={`order-row-${order.id}`}>
                      <TableCell>
                        <p className="font-medium">{order.order_number}</p>
                        <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                        {order.tracking_code && <p className="text-[10px] font-mono text-slate-400 mt-0.5">{order.tracking_code}</p>}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{(order.shipping_address?.first_name || order.billing_address?.first_name || '')} {(order.shipping_address?.last_name || order.billing_address?.last_name || '')}</p>
                        <p className="text-xs text-slate-500">{order.customer_email}</p>
                      </TableCell>
                      <TableCell>{order.items?.length || 0} article(s)</TableCell>
                      <TableCell className="font-semibold">{order.total?.toFixed(2)} €</TableCell>
                      <TableCell>
                        {(() => {
                          const kind = order.shipping_kind || (
                            /retrait|pickup|magasin|local_pickup|boutique/i.test(order.shipping_method_title || order.shipping_method || '') ? 'pickup'
                            : /relais/i.test(order.shipping_method_title || order.shipping_method || '') ? 'point_relay'
                            : (order.shipping_method ? 'delivery' : null)
                          );
                          const label = order.shipping_method_title || (order.shipping_method ? order.shipping_method.replace(/_/g, ' ') : '—');
                          if (kind === 'pickup') {
                            return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-amber-100 text-amber-800 font-medium" title={label}>🏪 Retrait</span>;
                          }
                          if (kind === 'point_relay') {
                            return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-purple-100 text-purple-800 font-medium" title={label}>📦 Point relais</span>;
                          }
                          if (kind === 'delivery') {
                            return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-blue-100 text-blue-800 font-medium" title={label}>🚚 Livraison</span>;
                          }
                          return <span className="text-xs text-slate-400">—</span>;
                        })()}
                      </TableCell>
                      <TableCell><Badge className={st.color}><StIcon size={14} className="mr-1" />{st.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }} data-testid={`view-order-${order.id}`}>
                          <Eye size={18} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersPage;
